import React, {useCallback} from 'react';
import SentryEchart from "../../../../components/echarts/sentry-echart.jsx";
import {scalePx} from "../../../../utils/responsive-graph.js";

const COMPACT_WIDTH = 340;

const DutyAttendancePriority = ({data, className, height}) => {
    const buildOption = useCallback((size) => {
        const s = Math.min(size.width, size.height);
        const isCompact = size.width < COMPACT_WIDTH;

        const labelFs = scalePx(s, 13, 315, 8, 24);
        const totalFs = scalePx(s, 13, 315, 8, 24);
        const legendFs = scalePx(s, 13, 315, 8, 22);
        const axisLabelWidth = scalePx(size.width, 87, 401, 40, 170);

        const data_ = Object.entries(data)
            .map(([key, values]) => {
                const total = (values?.inProgress || 0) +
                    (values?.onHold || 0) +
                    (values?.panic || 0) +
                    (values?.schedule || 0);

                return { key, values, total };
            })
            .sort((a, b) => b.total - a.total)
            .slice(0, 3);

        const inProgress = [];
        const onHold = [];
        const panic = [];
        const schedule = [];

        data_.forEach(({ values }) => {
            inProgress.push(values?.inProgress || 0);
            onHold.push(values?.onHold || 0);
            panic.push(values?.panic || 0);
            schedule.push(values?.schedule || 0);
        });

        const y = Array.from({ length: 3 }, (_, i) => {
            const key = data_[i]?.key;

            if (key == null) return "";
            return key || "Não informado";
        });

        const seriesStack = [schedule, inProgress, onHold, panic];
        const topSeriesIdxPerCategory = y.map((_, categoryIdx) => {
            for (let seriesIdx = seriesStack.length - 1; seriesIdx >= 0; seriesIdx--) {
                if (seriesStack[seriesIdx][categoryIdx] > 0) return seriesIdx;
            }
            return -1;
        });

        const totals = data_.map(d => d.total);
        const chartMax = Math.max(...totals, 1);

        const withTopRadius = (values, seriesIdx) => values.map((value, idx) => ({
            value,
            itemStyle: topSeriesIdxPerCategory[idx] === seriesIdx
                ? {borderRadius: [4, 4, 0, 0]}
                : undefined
        }));

        return {
            tooltip: {
                show: true,
                appendToBody: true,
                confine: false,
                extraCssText: 'max-width: 320px; white-space: normal;'
            },
            grid: {
                top: "15%",
                left: 0,
                right: !isCompact ? "25%" : 0,
                bottom: "5%",
                containLabel: true
            },
            legend: {
                name: "Situação",
                show: !isCompact,
                orient: 'vertical',
                right: 0,
                top: 'center',
                icon: 'circle',
                itemGap: 10,
                textStyle: {
                    color: '#FDFCFF',
                    fontSize: legendFs
                },
                data: ['Pânico', 'Aberto', 'Em andamento', 'Agendado']
            },
            xAxis: {
                type: 'category',
                data: y,
                axisLabel: {
                    color: '#F4FFFF',
                    fontSize: labelFs,
                    margin: 10,
                    width: axisLabelWidth,
                    overflow: 'truncate',
                    ellipsis: '...',
                    interval: 0
                },
                axisLine: {show: true},
                axisTick: {show: false}
            },
            yAxis: {
                type: 'value',
                axisLabel: {show: false},
                axisLine: {show: true},
                axisTick: {show: false},
                splitLine: {show: false}
            },
            series: [
                {
                    name: 'Agendado',
                    type: 'bar',
                    stack: 'situacao',
                    barWidth: '35%',
                    data: withTopRadius(schedule, 0),
                    itemStyle: {color: '#004893'}
                },
                {
                    name: 'Em andamento',
                    type: 'bar',
                    stack: 'situacao',
                    barWidth: '35%',
                    data: withTopRadius(inProgress, 1),
                    itemStyle: {color: '#187DD2'}
                },
                {
                    name: 'Aberto',
                    type: 'bar',
                    stack: 'situacao',
                    barWidth: '35%',
                    data: withTopRadius(onHold, 2),
                    itemStyle: {color: '#2ACBE2'}
                },
                {
                    name: 'Pânico',
                    type: 'bar',
                    stack: 'situacao',
                    barWidth: '35%',
                    data: withTopRadius(panic, 3),
                    itemStyle: {color: '#d9e8e8'}
                },
                {
                    name: '_total',
                    type: 'bar',
                    barWidth: '35%',
                    barGap: '-100%',
                    silent: true,
                    tooltip: {show: false},
                    itemStyle: {color: 'transparent'},
                    label: {
                        show: true,
                        position: 'top',
                        color: '#FDFCFF',
                        fontSize: totalFs,
                        fontWeight: 'bold',
                        formatter: (params) =>
                            totals[params.dataIndex] > 0 ? totals[params.dataIndex] : ''
                    },
                    data: totals.map(() => chartMax)
                }
            ]
        };
    }, [data]);

    return (
        <section className={className}>
            <SentryEchart
                option={buildOption}
                height={height}
                title={"Prioridade"}
            />
        </section>
    );
};

export default DutyAttendancePriority;
